import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import {
  MdOpenInFull, MdViewModule, MdArchitecture, MdPrecisionManufacturing,
  MdRule, MdExpandMore, MdChevronRight, MdSummarize,
} from 'react-icons/md';

// ── Value helpers ──────────────────────────────────────────────────────────────

const isBlank = (v) =>
  v === null || v === undefined || v === false || v === '' ||
  (typeof v === 'string' && (v.trim() === '' || v.trim() === '[]' || v.trim() === '{}')) ||
  (Array.isArray(v) && v.length === 0);

// Keys we never surface: identifiers, image/attachment/file references, timestamps.
const SKIP_KEY_RE = /(^id$|_id$|attached|image|logo|sketch|photo|signature|_file|file_|blob|url$|_url|created_at|updated_at)/i;

// Nicer labels for the most opaque keys; everything else is auto-humanized.
const LABEL_OVERRIDES = {
  loads_wind_speed: 'Wind speed', loads_on_frame: 'Live/frame', loads_live_on_purlins: 'Live/purlin',
  loads_collateral_on_frame: 'Collateral frame', loads_collateral_on_roof: 'Collateral roof',
  loads_rainfall: 'Rainfall', loads_seismic: 'Seismic', loads_occupancy_category: 'Occupancy',
  loads_recurrence_time: 'Recurrence', loads_explosure: 'Exposure', loads_description: 'Load note',
  building_parameters_frame_type: 'Frame', building_parameters_slope: 'Slope',
  building_parameters_bldgs_width: 'Width', building_parameters_width_module: 'Width module',
  building_parameters_length: 'Length', building_parameters_bay_spacing: 'Bay spacing',
  building_parameters_height: 'Height', building_parameters_height_other: 'Height (other)',
  building_parameters_column_support: 'Columns', building_parameters_note: 'Building note',
  building_usage: 'Usage', qty_of_identical_bldgs: 'Identical bldgs',
  purlin_girt_spacing: 'Purlin/girt spacing', top_coat_paint: 'Top coat paint', from_the_sea_: 'Distance from sea',
};

// Units to append for known numeric fields (data often stores the number only).
const UNIT_HINTS = {
  loads_wind_speed: ' km/h', loads_rainfall: ' mm/hr', loads_on_frame: ' kN/m²', loads_live_on_purlins: ' kN/m²',
  loads_collateral_on_frame: ' kN/m²', loads_collateral_on_roof: ' kN/m²',
};

const humanize = (key, prefix = '') => {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  const base = key.startsWith(prefix) ? key.slice(prefix.length) : key;
  const words = base.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

// ── Presentational primitives ──────────────────────────────────────────────────

// Renders a (possibly HTML) value safely. Plain strings pass through unchanged.
const HtmlValue = ({ html }) => (
  <span className="tw-font-medium tw-text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(html)) }} />
);
HtmlValue.propTypes = { html: PropTypes.any };

const Spec = ({ label, value, unit }) => {
  if (isBlank(value)) return null;
  return (
    <span className="tw-inline-flex tw-items-baseline tw-gap-1 tw-max-w-full">
      <span className="tw-text-slate-400 tw-flex-shrink-0">{label}</span>
      <HtmlValue html={unit ? `${value}${unit}` : value} />
    </span>
  );
};
Spec.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.any, unit: PropTypes.string };

const Flag = ({ label }) => (
  <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-bg-primary-50 tw-border tw-border-primary-100 tw-text-primary-600 tw-text-[11px] tw-font-medium">
    {label}
  </span>
);
Flag.propTypes = { label: PropTypes.string.isRequired };

// Renders every scalar/boolean field of `obj` (skipping blanks, ids, images and
// nested arrays/objects which are handled separately). Guarantees no populated
// value is hidden.
const Fields = ({ obj = {}, prefix = '', only = null, exclude = [] }) => {
  const entries = Object.entries(obj).filter(([key, value]) => {
    if (SKIP_KEY_RE.test(key)) return false;
    if (exclude.includes(key)) return false;
    if (only && !key.startsWith(only)) return false;
    if (Array.isArray(value) || (value && typeof value === 'object')) return false;
    return !isBlank(value);
  });
  if (entries.length === 0) return null;
  return (
    <>
      {entries.map(([key, value]) => (
        value === true
          ? <Flag key={key} label={humanize(key, prefix)} />
          : <Spec key={key} label={humanize(key, prefix)} value={value} unit={UNIT_HINTS[key]} />
      ))}
    </>
  );
};
Fields.propTypes = { obj: PropTypes.object, prefix: PropTypes.string, only: PropTypes.string, exclude: PropTypes.array };

const Group = ({ icon: Icon, title, color, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <Icon size={13} style={{ color }} />
        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide" style={{ color }}>{title}</span>
      </div>
      <div className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-pl-[18px]">{items}</div>
    </div>
  );
};
Group.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, color: PropTypes.string.isRequired, children: PropTypes.node };

// A repeating collection (cranes, mezzanines…) rendered as "Label (n)" + each item's fields.
const Collection = ({ label, items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-w-full">
      <span className="tw-text-slate-500 tw-font-medium">{label} ({items.length})</span>
      {items.map((item, i) => (
        <div key={item?.id || i} className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-pl-3 tw-text-xs">
          <span className="tw-text-slate-400">#{i + 1}</span>
          <Fields obj={item || {}} />
        </div>
      ))}
    </div>
  );
};
Collection.propTypes = { label: PropTypes.string.isRequired, items: PropTypes.array };

// BDS2 array fields get the Collection treatment; the rest fall through to Fields.
const BDS2_COLLECTIONS = [
  ['crane_informations', 'Cranes'], ['mezzanines', 'Mezzanines'], ['canopies', 'Canopies'],
  ['fascias', 'Fascias'], ['roof_extensions', 'Roof extensions'], ['frame_openings', 'Frame openings'],
];

// ── Key specs (curated important fields shown by default) ───────────────────────

const joinVals = (vals, sep = ' / ') => vals.filter((v) => !isBlank(v)).join(sep);

// The important fields the report must surface at a glance. Everything else lives
// behind the "Show all details" toggle. Blank fields self-hide (Spec returns null).
const KeySpecs = ({ bds1, bds2 }) => {
  const height = !isBlank(bds1.building_parameters_height)
    ? bds1.building_parameters_height
    : bds1.building_parameters_height_other;
  const dim    = [bds1.building_parameters_bldgs_width, bds1.building_parameters_length, height];
  const dimStr = dim.some((v) => !isBlank(v)) ? dim.map((v) => (isBlank(v) ? '–' : v)).join(' × ') : '';

  const canopies  = Array.isArray(bds2.canopies) ? bds2.canopies : [];
  const c0        = canopies[0] || {};
  const canopyStr = canopies.length
    ? `${canopies.length}× ${[c0.width, c0.length, c0.clear_height].map((v) => (isBlank(v) ? '–' : v)).join(' × ')}`
    : '';
  const mezz = Array.isArray(bds2.mezzanines) ? bds2.mezzanines : [];

  const roofInsul = joinVals([bds1.roof_name, !isBlank(bds1.roof_insulation_thickness) ? `${bds1.roof_insulation_thickness}mm` : null], ' ');
  const wallInsul = joinVals([bds1.wall_name, !isBlank(bds1.wall_insulation_thickness) ? `${bds1.wall_insulation_thickness}mm` : null], ' ');
  const ridge     = joinVals([bds2.ridge_vent_rv_type, !isBlank(bds2.ridge_vent_length) ? `L ${bds2.ridge_vent_length}` : null], ' · ');

  return (
    <Group icon={MdSummarize} title="Key specs" color="#4f46e5">
      <Spec label="Wind"       value={bds1.loads_wind_speed} unit=" km/h" />
      <Spec label="Rainfall"   value={bds1.loads_rainfall} unit=" mm/hr" />
      <Spec label="Frame"      value={bds1.building_parameters_frame_type} />
      <Spec label="Slope"      value={bds1.building_parameters_slope} />
      <Spec label="W×L×H"      value={dimStr} unit={dimStr ? ' m' : ''} />
      <Spec label="Bay"        value={bds1.building_parameters_bay_spacing} />
      <Spec label="Roof"       value={joinVals([bds1.roof_profile, bds1.roof_panel_is, bds1.roof_color])} />
      <Spec label="Wall"       value={joinVals([bds1.wall_profile, bds1.wall_panel_is, bds1.wall_color])} />
      <Spec label="Roof insul." value={roofInsul} />
      <Spec label="Wall insul." value={wallInsul} />
      <Spec label="Ridge vent"  value={ridge} />
      <Spec label="Canopy"      value={canopyStr} unit={canopyStr ? ' m' : ''} />
      <Spec label="Mezzanine"   value={mezz.length ? String(mezz.length) : ''} />
    </Group>
  );
};
KeySpecs.propTypes = { bds1: PropTypes.object, bds2: PropTypes.object };

// ── Area card (collapsible) ─────────────────────────────────────────────────────

const AreaCard = ({ area, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const bds1 = area.bds1 || {};
  const bds2 = area.bds2 || {};
  const bds3 = area.bds3 || {};

  return (
    <div className="tw-rounded tw-border tw-border-slate-200 tw-bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="tw-w-full tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-primary-600 tw-font-semibold tw-text-xs tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-bg-slate-50 tw-rounded"
      >
        {open ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
        <MdOpenInFull size={12} />
        Area {area.id}
        {!open && <span className="tw-ml-1 tw-font-normal tw-text-slate-400">
          {[bds1.building_parameters_frame_type, bds1.building_usage].filter(Boolean).join(' · ')}
        </span>}
      </button>

      {open && (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-pb-2.5 tw-pt-0.5">
          <KeySpecs bds1={bds1} bds2={bds2} />

          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            className="tw-self-start tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-primary-600 hover:tw-text-primary-700 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-px-0 tw-py-0.5"
          >
            {showAll ? <MdExpandMore size={14} /> : <MdChevronRight size={14} />}
            {showAll ? 'Hide details' : 'Show all details'}
          </button>

          {showAll && (<>
          <Group icon={MdArchitecture} title="BDS1 · Design & Loads" color="#0ea5e9"><Fields obj={bds1} only="loads_" prefix="loads_" /></Group>
          <Group icon={MdViewModule} title="BDS1 · Building" color="#6366f1">
            <Fields obj={bds1} only="building_parameters_" prefix="building_parameters_" />
            <Fields obj={bds1} exclude={Object.keys(bds1).filter((k) => k.startsWith('loads_') || k.startsWith('building_parameters_') || k.startsWith('roof_') || k.startsWith('wall_') || k.startsWith('grade_') || k.startsWith('eave_'))} />
          </Group>
          <Group icon={MdViewModule} title="BDS1 · Grade" color="#8b5cf6"><Fields obj={bds1} only="grade_" prefix="grade_" /></Group>
          <Group icon={MdViewModule} title="BDS1 · Roof" color="#0891b2"><Fields obj={bds1} only="roof_" prefix="roof_" /></Group>
          <Group icon={MdViewModule} title="BDS1 · Wall" color="#0d9488"><Fields obj={bds1} only="wall_" prefix="wall_" /></Group>
          <Group icon={MdViewModule} title="BDS1 · Eave" color="#65a30d"><Fields obj={bds1} only="eave_" prefix="eave_" /></Group>

          <Group icon={MdPrecisionManufacturing} title="BDS2 · Cranes & Accessories" color="#f59e0b">
            {BDS2_COLLECTIONS.map(([key, label]) => <Collection key={key} label={label} items={bds2[key]} />)}
            <Fields obj={bds2} exclude={BDS2_COLLECTIONS.map(([k]) => k)} />
          </Group>

          <Group icon={MdRule} title="BDS3 · Criteria & Environment" color="#10b981"><Fields obj={bds3} /></Group>
          </>)}
        </div>
      )}
    </div>
  );
};
AreaCard.propTypes = { area: PropTypes.object.isRequired, defaultOpen: PropTypes.bool };

// ── Root ─────────────────────────────────────────────────────────────────────

const PIFAreaTable = ({ data }) => {
  const areas = Array.isArray(data) ? data : [];
  if (areas.length === 0) return <span className="tw-text-slate-300 tw-text-xs">No area data</span>;
  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
      {areas.map((area, i) => <AreaCard key={area.id ?? i} area={area} defaultOpen={i === 0} />)}
    </div>
  );
};
PIFAreaTable.propTypes = { data: PropTypes.array };

export default PIFAreaTable;
